import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useNavigate, useParams } from 'react-router-dom'
import { useStore } from 'state'
import { useCanJoinEvent } from './useCanJoinEvent'
import { ReadEvent } from '../schemas/event-schemas'
import { useRedirectToCurrentEventState } from './use-redirect-to-current-event-state'

export const EVENT_KEY = 'event'

async function getEventById({ storeId, eventId }) {
  const url = new URL(
    `/v1/stores/${storeId}/events/${eventId}`,
    process.env.REACT_APP_EVENTS_SERVICE,
  )
  const response = await axios.get(url)
  const parseResult = ReadEvent.safeParse(response.data)
  if (!parseResult.success) {
    console.error('Event data validation failed:', parseResult.error)
    throw new Error('Invalid event data format')
  }
  return parseResult.data
}

export function useEventById() {
  const queryClient = useQueryClient()
  const { storeId, eventId } = useParams()
  const navigate = useNavigate()
  const { setEvent } = useStore()
  const { canJoin } = useCanJoinEvent()
  const { redirectToCurrentState } = useRedirectToCurrentEventState()
  const query = useQuery({
    queryKey: [EVENT_KEY, storeId, eventId],
    queryFn: async () => getEventById({ storeId, eventId }),
    onSuccess: (event) => {
      setEvent(event)

      redirectToCurrentState(event)
    },
    onError: (error) => {
      console.error('Error fetching current event:', error)
      return navigate(`/store/${storeId}/event/not-found`, { replace: true })
    },
    enabled: !!storeId,
    retry: 3,
    refetchOnMount: false,
  })

  const updateStartAtEvent = ({ secondsUntilStart }) => {
    queryClient.setQueryData([EVENT_KEY, storeId, eventId], (oldData) => {
      return {
        ...oldData,
        secondsUntilStart,
      }
    })
  }

  const updateFinishAtEvent = (newData) => {
    queryClient.setQueryData([EVENT_KEY, storeId, eventId], (oldData) => {
      return {
        ...oldData,
        ...newData,
      }
    })
  }

  return {
    ...query,
    canJoin,
    updateStartAtEvent,
    updateFinishAtEvent,
  }
}
